import { create } from 'zustand';

/**
 * Enroll-queue docked panel width — the ONE source of truth (U8, R10).
 *
 * Replaces the static `ENROLL_QUEUE_PANEL_WIDTH` constant that used to live
 * in `panel-layout.ts` (deleted): the width is now rep-draggable, so a
 * module constant can no longer express it. Default is `'35vw'`; a drag
 * commits a resolved pixel width clamped to
 * [`ENROLL_QUEUE_PANEL_MIN_WIDTH_PX`, `ENROLL_QUEUE_PANEL_MAX_WIDTH_FRACTION`
 * × viewport].
 *
 * ── THREE call sites that MUST stay coupled ──────────────────────────────
 *  - `shell-next/rail/RailDock.tsx` — the fixed (frameless-route) aside's own
 *    `width`
 *  - `AppRouter.tsx`'s `QueueContentShift` — the main content's
 *    `paddingRight` reflow
 *  - `GongFlowBrowse.tsx` — the portaled browse panel rendered AT the dock's
 *    width, on top of it
 *
 * A mismatch between any two of them is user-visible: a shift narrower than
 * the panel clips page content, a shift wider leaves a dead gap when the
 * panel closes, and a browse overlay that doesn't match the dock reads as a
 * rendering bug. All three read `useEnrollQueuePanelWidthCss()` — the SAME
 * derived CSS string — never their own copy of the math.
 *
 * ── Why zustand, and why no `persist` ────────────────────────────────────
 * Plain `create<State>()` module store per root CLAUDE.md ("Zustand for
 * Module-Scope Client State"): the toggle (the drag handle) lives in the
 * dock while the consumers are spread across the app shell, so they need
 * one reactive value. Deliberately NO `persist` middleware and no
 * localStorage: the width is SESSION-scoped (plan decision D) — a reload
 * returns every rep to the 35vw default rather than stranding someone in a
 * layout they dragged once and forgot about. This is the deliberate
 * difference from `zebraStripes.ts`, which IS persisted.
 *
 * State is data-only; the writers below are module-level functions, not
 * store members, so `useEnrollQueuePanelWidthStore.setState(initialState,
 * true)` (the CLAUDE.md test-reset contract) can't strip any behavior.
 */

/** Default width while nothing has been dragged. Viewport-relative on purpose — it re-tracks a window resize for free. */
export const ENROLL_QUEUE_PANEL_DEFAULT_WIDTH_CSS = '35vw';

/** Fraction of the default width, for resolving `35vw` to px when a drag starts. */
export const ENROLL_QUEUE_PANEL_DEFAULT_WIDTH_FRACTION = 0.35;

/** Lower drag bound (Spencer-confirmed 2026-08-31): below this the queue rows and rail stop being usable. */
export const ENROLL_QUEUE_PANEL_MIN_WIDTH_PX = 320;

/** Upper drag bound (Spencer-confirmed 2026-08-31): 75vw — wide enough to review/send inline (decision 13), never the whole screen. */
export const ENROLL_QUEUE_PANEL_MAX_WIDTH_FRACTION = 0.75;

export interface EnrollQueuePanelWidthState {
  /** Resolved dragged width in px, or `null` = "nobody has dragged" = the 35vw default. */
  widthPx: number | null;
}

/** Exported per the CLAUDE.md zustand convention — tests reset with `setState(initialState, true)`. */
export const initialState: EnrollQueuePanelWidthState = {
  widthPx: null,
};

export const useEnrollQueuePanelWidthStore = create<EnrollQueuePanelWidthState>(() => ({
  ...initialState,
}));

/**
 * Clamp a candidate width to the drag bounds for a given viewport width.
 *
 * Pure, and takes the viewport explicitly so it is testable without touching
 * `window`. When the viewport is too narrow to satisfy both bounds (below
 * ~427px, where 75vw < 320px) the UPPER bound wins: the panel must never
 * cover the entire screen, even if that means going under the nominal
 * minimum.
 */
export function clampPanelWidthPx(px: number, viewportWidthPx: number): number {
  const max = Math.round(viewportWidthPx * ENROLL_QUEUE_PANEL_MAX_WIDTH_FRACTION);
  return Math.min(Math.max(Math.round(px), ENROLL_QUEUE_PANEL_MIN_WIDTH_PX), max);
}

/** The ONE width→CSS derivation every consumer renders. `null` → the viewport-relative default. */
export function panelWidthCss(widthPx: number | null): string {
  return widthPx === null ? ENROLL_QUEUE_PANEL_DEFAULT_WIDTH_CSS : `${widthPx}px`;
}

/** Reactive read of the docked panel's width as a CSS length. All four coupled call sites use this. */
export function useEnrollQueuePanelWidthCss(): string {
  return useEnrollQueuePanelWidthStore((s) => panelWidthCss(s.widthPx));
}

function viewportWidth(): number {
  return typeof window === 'undefined' ? 0 : window.innerWidth;
}

/** The px width a drag should start from when nothing has been dragged yet (jsdom/collapsed-layout fallback). */
export function defaultPanelWidthPx(): number {
  return Math.round(viewportWidth() * ENROLL_QUEUE_PANEL_DEFAULT_WIDTH_FRACTION);
}

/** Commit a dragged width, clamped against the live viewport. Called on EVERY mousemove. */
export function setPanelWidthPx(px: number): void {
  useEnrollQueuePanelWidthStore.setState({ widthPx: clampPanelWidthPx(px, viewportWidth()) });
}

/** Back to the 35vw default. */
export function resetPanelWidth(): void {
  useEnrollQueuePanelWidthStore.setState({ widthPx: null });
}

/**
 * Re-clamp the stored width against the CURRENT viewport.
 *
 * A width dragged to 1200px on a wide monitor would otherwise survive a
 * shrink to an 800px window as 150vw of dock. `null` (the default) is left
 * alone — `35vw` re-tracks the viewport on its own.
 */
export function reclampPanelWidthToViewport(): void {
  const { widthPx } = useEnrollQueuePanelWidthStore.getState();
  if (widthPx === null) return;
  const next = clampPanelWidthPx(widthPx, viewportWidth());
  if (next !== widthPx) useEnrollQueuePanelWidthStore.setState({ widthPx: next });
}

// Module-scope resize listener (the `zebraStripes.ts` `storage`-listener
// precedent), deliberately NOT a component effect: the invariant "the stored
// width is always inside the bounds" belongs to the store, and must hold
// regardless of which consumer happens to be mounted. One listener for the
// app's lifetime, so there is nothing to clean up and no StrictMode
// double-invoke to guard against.
if (typeof window !== 'undefined') {
  window.addEventListener('resize', reclampPanelWidthToViewport);
}
